import { useTranslate } from "ra-core";
import type { ReactNode } from "react";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

// Plassholdere i samme form og størrelse som innholdet de står i for, slik at
// siden ikke står tom mens data hentes og ikke hopper når dataene kommer.
// Brukes i stedet for `if (isPending) return null`.

// Faste bredder i stedet for tilfeldige, så plassholderne ikke flimrer mellom
// rendringer og ser like ut i tester.
const WIDTHS = ["w-2/5", "w-1/3", "w-1/2", "w-2/5", "w-1/4", "w-1/3"];

const LoadingRegion = ({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) => {
  const translate = useTranslate();
  return (
    <div
      role="status"
      aria-busy="true"
      data-testid="loading-skeleton"
      className={className}
    >
      <span className="sr-only">
        {translate("ra.page.loading", { _: "Laster …" })}
      </span>
      {children}
    </div>
  );
};

/** Radliste som i Kunder og Kontakter: avkrysning, avatar og to linjer tekst. */
export const ListRowsSkeleton = ({ rows = 8 }: { rows?: number }) => (
  <LoadingRegion className="md:divide-y">
    {Array.from({ length: rows }, (_, i) => (
      <div
        key={i}
        className="flex flex-row items-center gap-4 py-1.5 pl-6 pr-4"
      >
        <Skeleton className="size-4 shrink-0 rounded-sm" />
        <Skeleton className="size-10 shrink-0 rounded-full" />
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <Skeleton className={cn("h-4", WIDTHS[i % WIDTHS.length])} />
          <Skeleton className="h-3 w-3/5" />
        </div>
      </div>
    ))}
  </LoadingRegion>
);

/** Filterspalte + kort med rader. Samme oppsett som listesidene. */
export const ListPageSkeleton = ({
  filter,
  // Nok rader til å fylle skjermen, så pagineringen havner under kanten og
  // ikke hopper ned når den ekte listen kommer.
  rows = 12,
}: {
  filter?: ReactNode;
  rows?: number;
}) => (
  <div className="flex w-full flex-col gap-4 md:flex-row md:gap-8">
    {filter}
    <div className="flex min-w-0 flex-1 flex-col gap-4">
      <Card className="py-0">
        <ListRowsSkeleton rows={rows} />
      </Card>
    </div>
  </div>
);

/** Tabell med overskriftsrad. */
export const TableSkeleton = ({
  rows = 8,
  columns = 5,
  className,
}: {
  rows?: number;
  columns?: number;
  className?: string;
}) => (
  <LoadingRegion className={cn("overflow-hidden rounded-md border", className)}>
    <div className="flex gap-4 border-b bg-muted/40 px-4 py-3">
      {Array.from({ length: columns }, (_, c) => (
        <Skeleton key={c} className="h-3.5 flex-1" />
      ))}
    </div>
    {Array.from({ length: rows }, (_, r) => (
      <div key={r} className="flex gap-4 border-b px-4 py-3 last:border-b-0">
        {Array.from({ length: columns }, (_, c) => (
          <div key={c} className="flex-1">
            <Skeleton
              className={cn(
                "h-4",
                c === 0 ? "w-4/5" : WIDTHS[(r + c) % WIDTHS.length],
              )}
            />
          </div>
        ))}
      </div>
    ))}
  </LoadingRegion>
);

/** Tavle (kanban) med kolonner og kort. */
export const BoardSkeleton = ({ columns = 5 }: { columns?: number }) => (
  <LoadingRegion className="flex gap-4 overflow-hidden">
    {Array.from({ length: columns }, (_, c) => (
      <div
        key={c}
        className="flex w-56 shrink-0 flex-col gap-3 rounded-lg bg-muted/40 p-3"
      >
        <Skeleton className="h-5 w-24" />
        <Skeleton className="h-3 w-20" />
        {Array.from({ length: 4 - (c % 2) }, (_, i) => (
          <div
            key={i}
            className="flex flex-col gap-2 rounded-md border bg-card p-3"
          >
            <Skeleton className="h-4 w-4/5" />
            <Skeleton className="h-4 w-3/5" />
            <Skeleton className="h-3 w-2/5" />
          </div>
        ))}
      </div>
    ))}
  </LoadingRegion>
);

/**
 * Detaljside: brødsmule, avatar og tittel, og så enten understrekede faner
 * rett på siden (som kundekortet) eller et kort med innhold (uten faner).
 */
export const ShowPageSkeleton = ({
  avatar = true,
  tabs = 5,
}: {
  avatar?: boolean;
  tabs?: number;
}) => {
  const lines = (
    <div className="flex flex-col gap-3">
      {Array.from({ length: 5 }, (_, i) => (
        <Skeleton key={i} className={cn("h-4", i % 2 ? "w-3/4" : "w-full")} />
      ))}
    </div>
  );
  return (
    <LoadingRegion className="flex flex-col gap-4">
      <Skeleton className="h-3.5 w-20" />
      <div className="flex items-center gap-3">
        {avatar && <Skeleton className="size-10 shrink-0 rounded-full" />}
        <Skeleton className="h-6 w-64" />
      </div>
      {tabs > 0 ? (
        <>
          <div className="flex h-10 items-center gap-6 border-b">
            {Array.from({ length: tabs }, (_, i) => (
              <Skeleton key={i} className="h-4 w-16" />
            ))}
          </div>
          {lines}
        </>
      ) : (
        <Card className="p-6">{lines}</Card>
      )}
    </LoadingRegion>
  );
};

/** Skjema: etikett + felt, gjentatt. */
export const FormSkeleton = ({ fields = 6 }: { fields?: number }) => (
  <LoadingRegion className="flex flex-col gap-4">
    <Skeleton className="h-3.5 w-20" />
    <Skeleton className="h-6 w-56" />
    <Card className="flex flex-col gap-5 p-6">
      {Array.from({ length: fields }, (_, i) => (
        <div key={i} className="flex flex-col gap-2">
          <Skeleton className="h-3.5 w-24" />
          <Skeleton className="h-9 w-full" />
        </div>
      ))}
    </Card>
  </LoadingRegion>
);
